import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useAudioContext } from '@/contexts/AudioContext';
import { useWelcome } from '@/contexts/WelcomeContext';
import { Maximize2, Minimize2, PanelLeftClose, Monitor, AlignVerticalSpaceAround, RotateCcw, Activity } from 'lucide-react';
import { AudioPerformanceDashboard } from '@/components/AudioPerformanceDashboard';
import { isElectron, isDashboardAccessible, isDashboardAccessibleAsync } from '@/utils/environment';

const VITE_SHOW_KOFI_BUTTON = import.meta.env.VITE_SHOW_KOFI_BUTTON === 'true';
const VITE_KOFI_URL = import.meta.env.VITE_KOFI_URL || 'https://ko-fi.com/bliponskrine';

// Debug logging - remove after testing
console.log('Environment Variables:', {
  VITE_SHOW_KOFI_BUTTON: import.meta.env.VITE_SHOW_KOFI_BUTTON,
  VITE_KOFI_URL: import.meta.env.VITE_KOFI_URL,
  VITE_SHOW_PATREON_BUTTON: import.meta.env.VITE_SHOW_PATREON_BUTTON,
  computed_VITE_SHOW_KOFI_BUTTON: VITE_SHOW_KOFI_BUTTON
});

function KofiIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
    >
      <path d="M23.881 8.948c-.773-4.085-4.859-4.593-4.859-4.593H.723c-.604 0-.679.798-.679.798s-.082 7.324-.033 11.596c.049 4.271 3.093 7.637 7.312 7.637s8.033-2.988 8.033-8.033v-.798c0-.994.821-1.807 1.815-1.807.994 0 1.815.813 1.815 1.807v.798c0 5.045 4.988 8.033 8.033 8.033s7.312-3.366 7.312-7.637c.049-4.272-.033-11.596-.033-11.596s-.075-.798-.679-.798zM7.237 14.212c-1.24 0-2.25-1.007-2.25-2.25s1.01-2.25 2.25-2.25 2.25 1.007 2.25 2.25-1.01 2.25-2.25 2.25z" />
    </svg>
  );
}

function SonicGrooveLogo() {
  return (
    <div className="relative w-10 h-10 bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 rounded-xl p-2 shadow-lg">
      <div className="absolute inset-1 flex items-center justify-center">
        <div className="flex items-end gap-0.5 h-6">
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [8, 16, 12, 20, 8] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [12, 8, 20, 10, 16] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [16, 20, 8, 16, 12] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [10, 12, 16, 8, 20] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
          />
          <motion.div 
            className="w-0.5 bg-white rounded-full"
            animate={{ height: [20, 8, 12, 16, 10] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
          />
        </div>
      </div>
      
      <div className="absolute inset-0 bg-gradient-to-br from-purple-400/20 via-blue-400/20 to-cyan-400/20 rounded-xl blur-sm"></div>
    </div>
  );
}

const visualizerColors = {
  cosmic: {
    primary: 'from-purple-400 via-blue-400 to-cyan-400',
    glow: 'drop-shadow-[0_0_20px_rgba(147,51,234,0.5)]'
  },
  mirror: {
    primary: 'from-pink-400 via-purple-400 to-blue-400',
    glow: 'drop-shadow-[0_0_20px_rgba(236,72,153,0.5)]'
  },
  frosty: {
    primary: 'from-cyan-400 via-blue-400 to-indigo-400',
    glow: 'drop-shadow-[0_0_20px_rgba(34,211,238,0.5)]'
  },
  dual: {
    primary: 'from-purple-400 via-green-400 to-blue-400',
    glow: 'drop-shadow-[0_0_20px_rgba(168,85,247,0.5)]'
  },
  ambient: {
    primary: 'from-orange-400 via-yellow-400 to-red-400',
    glow: 'drop-shadow-[0_0_20px_rgba(251,146,60,0.5)]'
  },
  sandy: {
    primary: 'from-yellow-400 via-orange-400 to-red-400',
    glow: 'drop-shadow-[0_0_20px_rgba(251,191,36,0.5)]'
  },
  randomize: {
    primary: 'from-rainbow-400 via-rainbow-500 to-rainbow-600',
    glow: 'drop-shadow-[0_0_20px_rgba(147,51,234,0.5)]'
  }
};

function StreamerModeHeaderTitle({ currentSong, isPlaying, visualizerType }) {
  if (!currentSong) return null;

  const songTitle = currentSong?.title || currentSong?.name || 'No Song Playing';
  const colors = visualizerColors[visualizerType] || visualizerColors.cosmic;

  return (
    <div className="flex items-center gap-3 flex-1 justify-center max-w-2xl mx-4">
      <motion.div
        key={songTitle}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: 1,
          filter: isPlaying ? 'brightness(1.2)' : 'brightness(0.8)'
        }}
        transition={{ 
          duration: 0.4, 
          ease: "easeOut",
          filter: { duration: 0.3 }
        }}
        className="text-center"
      >
        <h2 
          className={`text-xl md:text-2xl font-bold bg-gradient-to-r ${colors.primary} bg-clip-text text-transparent ${colors.glow} leading-tight truncate`}
          style={{
            textShadow: isPlaying ? '0 0 20px rgba(255,255,255,0.2)' : 'none',
            filter: `${colors.glow} ${isPlaying ? 'brightness(1.2)' : 'brightness(0.8)'}`
          }}
        >
          {songTitle}
        </h2>
        
        <div className="flex items-center justify-center gap-2 mt-1">
          <div className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-green-400' : 'bg-gray-400'}`}>
            {isPlaying && (
              <motion.div
                className="w-1.5 h-1.5 rounded-full bg-green-400"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [1, 0.6, 1]
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            )}
          </div>
          <span className="text-xs text-white/60 font-medium">
            {isPlaying ? 'NOW PLAYING' : 'PAUSED'}
          </span>
        </div>
      </motion.div>

      {isPlaying && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          animate={{
            opacity: [0.2, 0.4, 0.2],
            scale: [1, 1.01, 1]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div 
            className={`w-full h-full bg-gradient-to-r ${colors.primary} opacity-10 blur-xl rounded-lg`}
            style={{
              filter: 'blur(20px)',
              transform: 'scale(1.2)'
            }}
          />
        </motion.div>
      )}
    </div>
  );
}

function HeartIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <defs>
        <linearGradient id="heartGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#ff5f6d" />
          <stop offset="100%" stopColor="#ffc371" />
        </linearGradient>
      </defs>
      <path d="M12 21s-6-4.35-9-8.28C-1.5 7.36 4.5 2.5 8.5 6.28c2.1 2.02 3.5 2.02 5.5 0C19.5 2.5 25.5 7.36 21 12.72 18 16.65 12 21 12 21z" fill="url(#heartGradient)" stroke="url(#heartGradient)" />
    </svg>
  );
}

// Ko-fi Widget Component
function KofiWidget() {
  return (
    <a href='https://ko-fi.com/E1E31K05AC' target='_blank' rel="noopener noreferrer">
      <Button
        variant="ghost"
        size="sm"
        className="bg-gradient-to-r from-blue-400 to-cyan-400 text-white shadow-md hover:from-blue-500 hover:to-cyan-500 hover:shadow-lg flex items-center gap-2 border-0 px-3 py-1.5 rounded-full font-semibold transition-all duration-200"
        title="Support us on Ko-fi"
        style={{
          background: 'linear-gradient(90deg, #29abe0 0%, #00d4ff 100%)',
          color: 'white',
          border: 'none',
          boxShadow: '0 2px 8px 0 rgba(41, 171, 224, 0.15)'
        }}
      >
        <KofiIcon className="h-4 w-4" />
        <span className="hidden sm:inline">Support</span>
      </Button>
    </a>
  );
}

export function AppHeader() {
  const {
    isPlayerMinimized,
    setIsPlayerMinimized,
    isPlaylistCollapsed,
    togglePlaylist,
    isStreamerMode,
    setIsStreamerMode,
    currentSong,
    isPlaying,
    visualizerType,
    lyricsPosition,
    toggleLyricsPosition
  } = useAudioContext();

  const { resetWelcome } = useWelcome();

  // State for audio performance dashboard
  const [showAudioDashboard, setShowAudioDashboard] = useState(false);
  const [canAccessDashboard, setCanAccessDashboard] = useState(false);

  // Development flags
  const isDev = import.meta.env.DEV;
  
  // Use environment utility for Electron detection
  const isElectronRuntime = isElectron();

  // Check dashboard accessibility (async for Electron .env support)
  useEffect(() => {
    const checkDashboardAccess = async () => {
      if (isElectronRuntime) {
        // Use async version for Electron to check .env file
        const hasAccess = await isDashboardAccessibleAsync();
        setCanAccessDashboard(hasAccess);
      } else {
        // Use sync version for web
        setCanAccessDashboard(isDashboardAccessible());
      }
    };
    
    checkDashboardAccess();
  }, [isElectronRuntime]);

  const handleStreamerModeToggle = () => {
    console.log('🎬 Toggling Streamer Mode:', !isStreamerMode);
    setIsStreamerMode(!isStreamerMode);
  };

  const handlePlaylistToggle = () => {
    console.log('🎯 AppHeader: Playlist toggle clicked, current state:', isPlaylistCollapsed);
    togglePlaylist();
  };

  return (
    <motion.header 
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className={`flex items-center justify-between p-3 md:p-4 border-b transition-all duration-300 relative ${
        isStreamerMode 
          ? 'bg-black border-white/5' 
          : 'bg-black/20 backdrop-blur-sm border-white/10'
      }`}
    >
      <div className="flex items-center gap-2 md:gap-3">
        <SonicGrooveLogo />
        <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
          Sonic Groove
        </h1>
        {VITE_SHOW_KOFI_BUTTON && !isStreamerMode && (
          <div className="ml-2">
            <KofiWidget />
          </div>
        )}
      </div>

      {isStreamerMode && (
        <StreamerModeHeaderTitle 
          currentSong={currentSong}
          isPlaying={isPlaying}
          visualizerType={visualizerType}
        />
      )}
      
      <div className="flex items-center gap-1 md:gap-2">
        {!isStreamerMode && (
            <Button
                variant="ghost"
                size="sm"
                onClick={toggleLyricsPosition}
                className="text-white hover:bg-white/10"
                title={`Toggle Lyrics Position (Current: ${lyricsPosition === 'top' ? 'Top' : 'Bottom'})`}
            >
                <AlignVerticalSpaceAround className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Lyrics: {lyricsPosition === 'top' ? 'Top' : 'Bottom'}</span>
            </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleStreamerModeToggle}
          className={`text-white hover:bg-white/10 transition-colors ${
            isStreamerMode ? 'bg-red-500/20 text-red-400 border border-red-400/30' : ''
          }`}
          title="Toggle Streamer Mode"
        >
          <Monitor className="h-4 w-4 md:mr-2" />
          <span className="hidden md:inline">Streamer</span>
        </Button>
        {!isStreamerMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePlaylistToggle}
            className="text-white hover:bg-white/10"
            title="Toggle Playlist"
          >
            <PanelLeftClose className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Playlist</span>
          </Button>
        )}
        {!isStreamerMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsPlayerMinimized(!isPlayerMinimized)}
            className="text-white hover:bg-white/10"
          >
            {isPlayerMinimized ? <Maximize2 className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
          </Button>
        )}
        
        {/* Development Welcome Reset Button */}
        {isDev && !isElectronRuntime && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => resetWelcome()}
            className="text-white hover:bg-white/10"
            title="Reset Welcome Modal (Dev Only)"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}
        
        {/* Audio Performance Dashboard Button - Only in Electron & Dev/Test builds */}
        {isElectronRuntime && canAccessDashboard && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAudioDashboard(true)}
            className="text-white hover:bg-white/10"
            title="Audio Performance Dashboard (Dev/Test Only)"
          >
            <Activity className="h-4 w-4" />
          </Button>
        )}
      </div>
      
      {/* Audio Performance Dashboard - Only in Electron & Dev/Test builds */}
      {isElectronRuntime && canAccessDashboard && (
        <AudioPerformanceDashboard
          isOpen={showAudioDashboard}
          onClose={() => setShowAudioDashboard(false)}
        />
      )}
    </motion.header>
  );
}